import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Download, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PageHeader, Spinner } from "@/components/common/Common";
import { StatusBadge } from "@/components/common/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import { api, fmtDate, formatError } from "@/lib/api";
import { Navigate } from "react-router-dom";

export default function Reports() {
  const { canWrite } = useAuth();
  const [rows, setRows] = useState(null);
  const [sel, setSel] = useState([]);
  const [exported, setExported] = useState(null);
  const load = () => api.get("/reports/payroll-handoff").then((r) => { setRows(r.data); setSel(r.data.map((x) => x.id)); });
  useEffect(() => { if (canWrite) load(); }, [canWrite]);
  if (!canWrite) return <Navigate to="/dashboard" replace />;

  const exportCsv = async () => {
    try {
      const res = await api.post("/reports/payroll-handoff/export", { ids: sel }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      Object.assign(document.createElement("a"), { href: url, download: `payroll-handoff-${new Date().toISOString().slice(0, 10)}.csv` }).click();
      URL.revokeObjectURL(url);
      setExported([...sel]);
    } catch (e) { toast.error(formatError(e)); }
  };
  const mark = async () => {
    try { const { data } = await api.post("/reports/payroll-handoff/mark-setup", { ids: exported }); toast.success(`${data.marked} contract(s) marked as set up in payroll`); setExported(null); load(); } catch (e) { toast.error(formatError(e)); }
  };
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  return (
    <div>
      <PageHeader title="Reports" subtitle="Every list in the app can be exported to CSV from its page. Exports are recorded in the audit log." testId="reports-header" />
      <section className="bg-white border border-slate-200 rounded-lg shadow-sm" data-testid="payroll-handoff">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-5 py-4 border-b border-slate-100">
          <div>
            <h3 className="text-lg font-semibold text-slate-800">Payroll handoff</h3>
            <p className="text-xs text-slate-500 flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />Ready for payroll, not yet set up. Download only, never emailed. Contains full bank details.</p>
          </div>
          <Button onClick={exportCsv} disabled={!sel.length} data-testid="payroll-export-button"><Download className="h-4 w-4 mr-1" />Export {sel.length} to CSV</Button>
        </div>
        {!rows ? <div className="p-8 flex justify-center"><Spinner /></div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500 text-left">
                <th className="px-4 py-3 w-10"><Checkbox checked={rows.length > 0 && sel.length === rows.length} onCheckedChange={(c) => setSel(c ? rows.map((r) => r.id) : [])} data-testid="payroll-select-all" /></th>
                {["Employment ID", "Contractor", "Recruitment company", "Status", "Start", "Assigned to"].map((h) => <th key={h} className="px-4 py-3 font-medium">{h}</th>)}
              </tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t border-slate-100" data-testid={`payroll-row-${r.id}`}>
                    <td className="px-4 py-3"><Checkbox checked={sel.includes(r.id)} onCheckedChange={() => toggle(r.id)} data-testid={`payroll-select-${r.id}`} /></td>
                    <td className="px-4 py-3 font-mono text-xs font-semibold"><Link to={`/contracts/${r.id}`} className="hover:text-blue-700">{r.employment_id}</Link></td>
                    <td className="px-4 py-3">{r.contractor_name}</td>
                    <td className="px-4 py-3 text-slate-600">{r.recruitment_company_name}</td>
                    <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                    <td className="px-4 py-3">{fmtDate(r.start_date)}</td>
                    <td className="px-4 py-3 text-slate-600">{r.assigned_to_name || "—"}</td>
                  </tr>
                ))}
                {!rows.length && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400" data-testid="payroll-empty">No contracts are waiting for payroll set-up</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </section>
      <Dialog open={!!exported} onOpenChange={(o) => !o && setExported(null)}>
        <DialogContent data-testid="payroll-mark-dialog">
          <DialogHeader><DialogTitle>Mark these as set up in payroll?</DialogTitle>
            <DialogDescription>This ticks “Set up in payroll system” on the {exported?.length} exported contract(s). Only do this once they are loaded into payroll.</DialogDescription></DialogHeader>
          <DialogFooter><Button variant="outline" onClick={() => setExported(null)} data-testid="payroll-mark-no">Not yet</Button><Button onClick={mark} data-testid="payroll-mark-yes">Yes, mark as set up</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
